import type {NavigateFunction} from 'react-router-dom'
import {v4 as uuidv4} from 'uuid'
import {getLogger} from '@/shared/utils'
import {collectTermUris, type FileTags} from '@/shared/utils/ontology/tags'
import {getFileS3Url, getFileTags} from '../../core/_requests'
import {shareFileS3Url, type ShareRoot} from '../../core/storage'
import {getFileMetadata} from '../../services/fileMetadata'
import {resolveViewerVersion} from '../../services/viewerManifest'
import {
  createCloseViewerByIdMessage,
  createOpenLoadingViewerMessage,
  createRequestViewerMessage,
  createViewerErrorMessage,
  DEFAULT_VIEWER_LOADING_MESSAGE,
  type ExternalViewerConfig,
  type ViewerErrorKind,
  ViewerDataSource,
  ViewerMode,
} from './handler-structures'
import {plainFileFormat} from './plain-file-formats'
import {VIEWER_METADATA_SCHEMA} from './viewerMetadataRefresh'

/** Web-component that renders plain documents, and the menu name shown for it. */
const DOCUMENT_VIEWER_APP = 'html-viewer'
const DOCUMENT_VIEWER_NAME = 'Document Viewer'

/** A failure this module explains itself: its message is written for the user and safe to show. */
class ViewerOpenError extends Error {
  constructor(message: string, readonly kind: ViewerErrorKind) {
    super(message)
  }
}

/**
 * Open a file in a web viewer with optimistic feedback (B1). Shared by the file-manager
 * double-click and by viewer-opening context-menu actions (B2) so the UX is identical:
 *
 *  1. immediately open a "loading" placeholder tab and switch to Results, so the user gets
 *     instant feedback instead of a frozen UI while the OBserver resolves the viewer;
 *  2. resolve the viewer. A plain document (png/pdf/txt — see {@link plainFileFormat}) short-circuits
 *     here and is resolved entirely in the browser; everything else requests `/metadata`, sending the
 *     file's most-specific ontology format term so the OBserver can resolve the viewer without
 *     reading/loading the bytes;
 *  3. replace the placeholder in place with the real viewer (correlation id), route workflow
 *     objects to their own page, or tear the placeholder down on error.
 *
 * The Results page and its viewer manager are always mounted, so the messages posted here are
 * received even before navigation completes.
 */
export async function openFileInViewer(params: {
  /**
   * Storage file name / id (e.g. the file-manager row id): relative to the user's own root, or to
   * {@link share} when that is set.
   */
  filename: string
  /**
   * The folder someone else shares that `filename` is in. The file is then read through the share:
   * its URI names the owner's key, its bytes are presigned by the sharing API, and the OBserver is
   * told the share's id. Absent for the user's own files, including folders they share.
   */
  share?: ShareRoot
  /** Clean display name for the tab (e.g. the file-manager row name); falls back to filename. */
  displayName?: string
  /** The file's ontology tags, used to derive the format term hint. */
  tags?: FileTags
  navigate: NavigateFunction
  /** Placeholder message; callers configure it (e.g. a menu action's own wording). */
  loadingMessage?: string
  /**
   * Bring back the centre column if Files+Chat had collapsed it. Same-route opens (already on
   * /results) do not change pathname, so callers must expand here rather than relying on a
   * navigation effect.
   */
  expandCenter?: () => void
}): Promise<void> {
  const {
    filename,
    share,
    displayName,
    navigate,
    loadingMessage = DEFAULT_VIEWER_LOADING_MESSAGE,
    expandCenter,
  } = params
  // Correlation id links the optimistic placeholder to the real config that replaces it once
  // /metadata resolves — independent of the server-assigned objectInfo.id.
  const correlationId = uuidv4()
  // Tab title: basename without the .box extension (matches the object's project name), so the
  // placeholder tab does not show the storage prefix or the .box suffix.
  const tabName = tabDisplayName(displayName ?? filename)

  try {
    window.postMessage(
      createOpenLoadingViewerMessage({correlationId, name: tabName, loadingMessage})
    )
    expandCenter?.()
    navigate('/results')

    const tags = params.tags ?? (share ? undefined : await lookUpFileTags(filename))
    const termUris = collectTermUris(tags)
    const isBoxFile = filename.toLowerCase().endsWith('.box')
    const edamFormatTerm = termUris.find((uri) => uri.startsWith('edam:format_'))

    // Plain document: open it without involving the OBserver at all. Nothing it could tell us is
    // needed — the extension identifies the viewer, the manifest gives its version, and the browser
    // reads the bytes itself from a presigned URL. A round trip here only added latency and, for
    // these formats, a 404.
    //
    // The ontology still wins when it actually knows something: a file carrying an EDAM *format*
    // term or a Lambda format id has been content-sniffed (a `.txt` that is really a TSV, say), and
    // those keys are exactly what the OBserver's raw-viewer registry resolves by — so let it. Only
    // files the tagger had no format opinion on take this path.
    const plainFormat = !isBoxFile && !edamFormatTerm && !tags?.format
      ? plainFileFormat(filename)
      : undefined
    if (plainFormat) {
      await openPlainFileInViewer({
        filename,
        share,
        tags,
        format: plainFormat,
        tabName,
        correlationId,
        loadingMessage,
      })
      return
    }

    // Pass the file's ontology format term so the OBserver resolves the viewer without reading the
    // bytes.
    //  - raw (non-.box) files: prefer an EDAM format CURIE (matched against the raw viewer
    //    creators), falling back to the most-specific term, then the plain format id.
    //  - .box files: prefer an omicsbox:format_* term over an omicsbox:data_* one. In practice the
    //    format branch tags carry the object's own concrete leaf term (e.g. Krona's own
    //    omicsbox:format_0703, not the broader omicsbox:format_0700 "taxonomic classification
    //    result" it also happens to register under). The data branch is routinely a shared
    //    category tag reused by several unrelated sibling classes (Krona/Tcr/BarChart/MLST all
    //    carry the same omicsbox:data_0701 "taxonomic classification data" term) — resolving by it
    //    is ambiguous server-side (ObjectControllersManager.resolveByFormatTerm picks whichever
    //    registered class it finds first, silently opening the wrong viewer). Do NOT fall back to
    //    termUris[0] blindly: collectTermUris only reflects the tagging Lambda's emission order,
    //    which is not guaranteed to put the unambiguous term first (this guards against exactly
    //    that — a Krona chart resolving to the MLST viewer because data_0701 was tagged first).
    //    With no omicsbox:format_* term, send none: the OBserver then loads the object and picks
    //    its viewer from the real class. EDAM terms are just as shared — edam:data_0928 maps to
    //    every bulk and single-cell DE result and to single-cell charts, so a DESeq2 result
    //    opened by it landed in chart-viewer and stayed blank.
    const formatHint = {
      formatTerm: isBoxFile
        ? termUris.find((uri) => uri.startsWith('omicsbox:format_'))
        : edamFormatTerm ?? termUris[0],
      formatId: tags?.format,
    }
    const jsonResponse = await (share
      ? getFileMetadata(filename, formatHint, share)
      : getFileMetadata(filename, formatHint))

    // A metadata response with no resolvable viewer is a failure, even though it arrived as a 200.
    // The OBserver now 404s this case, but older/other backends answer 200 with an empty
    // controllerInfo — and posting that config anyway put a permanently broken tab on screen whose
    // only trace was "no viewer could be resolved for object type X" in the console. Throwing routes
    // it through the catch below, so the tab the user already opened explains itself.
    if (!jsonResponse.viewerURL && !jsonResponse.controllerInfo?.viewerAppName) {
      throw new ViewerOpenError(
        `There is no web viewer for this kind of file yet${
          jsonResponse.objectInfo?.type ? ` (${jsonResponse.objectInfo.type})` : ''
        }.`,
        'unsupported'
      )
    }

    const fileType = jsonResponse.objectInfo.type.toLowerCase()
    // initializing: keep the spinner as an overlay over the freshly-mounted iframe until the viewer
    // signals it is ready to load data, so there is no blank flash on the placeholder→viewer swap.
    const config = {
      ...jsonResponse,
      correlationId,
      initializing: true,
      loadingMessage,
      // Retain the source cloud path so the AI chat can reference this object as the "focused result",
      // and so a restored tab can re-request its metadata.
      sourcePath: filename,
      // Keep the tags so the tab shows the file's type icon.
      ...(tags ? {tags} : {}),
      // Stamp the contract this config was resolved against, and keep the hint that resolved it: a
      // config persisted to localStorage is never re-fetched on its own, so this is how a restored
      // tab can tell that its controllerInfo is older than what the frontend now reads — and take
      // the cheap ontology fast-path when refreshing it. See viewerMetadataRefresh.
      metadataSchema: VIEWER_METADATA_SCHEMA,
      formatHint,
      ...(share ? {share} : {}),
    } as unknown as ExternalViewerConfig

    if (fileType === 'workflow' || fileType === 'workflowsanalysisobject') {
      // Workflows render on their own page (mounted on demand); close the Results placeholder and
      // hand the config to WorkflowsPage via router state, as before.
      window.postMessage(createCloseViewerByIdMessage(correlationId))
      navigate('/workflows', {state: {viewerConfig: jsonResponse, openViewer: true}})
      return
    }

    // Result viewer: replace the placeholder in place via the correlation id.
    window.postMessage(createRequestViewerMessage(config))
  } catch (error) {
    getLogger().error('Failed to open file:', error)
    // Show the error inside the tab that was already opened, rather than silently closing it — the
    // user's click always results in a visible, explained outcome.
    const {message, kind} = describeOpenError(error)
    window.postMessage(createViewerErrorMessage(correlationId, tabName, message, kind))
  }
}

/**
 * What the tab says for errors from elsewhere. Fixed sentences on purpose: such a message can be a
 * raw OBserver response that spells out the file's full storage URI (bucket, identity id), which is
 * never shown to the user. The full error goes to the log instead.
 */
const VIEWER_ERROR_DETAILS: Record<ViewerErrorKind, string> = {
  unsupported: 'There is no web viewer for this kind of file yet.',
  failed: 'Something went wrong while opening it. Try again, or contact support if this keeps happening.',
}

/**
 * The tab's message and heading kind for a failed open. A 404 from /metadata ("No raw web viewer
 * for file …") is how the OBserver says it has no viewer for the file, i.e. an unsupported type.
 */
function describeOpenError(error: unknown): {message: string; kind: ViewerErrorKind} {
  if (error instanceof ViewerOpenError) return {message: error.message, kind: error.kind}
  const kind: ViewerErrorKind =
    (error as {response?: {status?: number}} | null)?.response?.status === 404 ? 'unsupported' : 'failed'
  return {message: VIEWER_ERROR_DETAILS[kind], kind}
}

/**
 * The file's ontology tags when the caller had only its path (a `cloud:` link in a chat answer, a
 * `/results?file=` deep link). Without them the OBserver gets no format hint and cannot resolve a
 * raw file such as a CSV. A failed lookup only loses the hint, so it does not fail the open.
 */
async function lookUpFileTags(filename: string): Promise<FileTags | undefined> {
  try {
    return await getFileTags(filename)
  } catch (error) {
    getLogger().warn('Could not read the file tags; opening without a format hint:', error)
    return undefined
  }
}

/**
 * Build and post the viewer config for a plain document, entirely client-side.
 *
 * Mirrors the shape the OBserver returns for a raw file so the two are interchangeable downstream:
 * {@code objectInfo.id} is the storage URI (the managers dedupe tabs by it, so a file opened this way
 * and the same file opened via `/metadata` land on one tab), and {@code objectInfo.type} carries the
 * format, which is what the manager forwards to the viewer as the {@code format} of its `getApiUrl`
 * reply.
 *
 * Throws when the viewer version cannot be resolved, so the caller's catch turns it into an explained
 * error tab rather than an iframe pointed at a URL that does not exist.
 */
async function openPlainFileInViewer(params: {
  filename: string
  share?: ShareRoot
  tags?: FileTags
  format: string
  tabName: string
  correlationId: string
  loadingMessage: string
}): Promise<void> {
  const {filename, share, tags, format, tabName, correlationId, loadingMessage} = params
  // Not an OBserver call. The owner's key for a shared file (presigned through the share later);
  // the user's own key otherwise.
  const objectUri = share ? shareFileS3Url(share, filename) : await getFileS3Url(filename)
  const viewerVersion = await resolveViewerVersion(DOCUMENT_VIEWER_APP)
  if (!viewerVersion) {
    throw new ViewerOpenError(
      'The viewer for this file type is not available yet. Please try again later.',
      'failed'
    )
  }

  const config: ExternalViewerConfig = {
    objectInfo: {
      id: objectUri,
      name: tabName,
      type: format,
      // Where the browser reads the bytes from; required for the manager's STORAGE branch.
      objectUri,
    },
    controllerInfo: {
      // No server-side controller exists for these files; the fields stay empty for the same reason
      // the OBserver leaves them empty on its own raw-file responses.
      controllerClass: '',
      controllerEndpoint: '',
      objectClass: '',
      viewerName: DOCUMENT_VIEWER_NAME,
      viewerAppName: DOCUMENT_VIEWER_APP,
      viewerVersion,
      dataSource: ViewerDataSource.STORAGE,
      // No exports: the viewer renders bytes it does not own, and the STORAGE path never probes for
      // export capabilities anyway.
      workbenchProperties: [],
    },
    viewerURL: '',
    mode: ViewerMode.READ,
    correlationId,
    initializing: true,
    loadingMessage,
    sourcePath: filename,
    ...(tags ? {tags} : {}),
    // Current by construction: this config is built here, not from a /metadata answer, so it is
    // never a candidate for a refresh on restore.
    metadataSchema: VIEWER_METADATA_SCHEMA,
    ...(share ? {share} : {}),
  }

  window.postMessage(createRequestViewerMessage(config))
}

/** Basename (drop any storage prefix) without a trailing {@code .box} extension, for tab titles. */
function tabDisplayName(nameOrPath: string): string {
  const base = nameOrPath.substring(nameOrPath.lastIndexOf('/') + 1)
  return base.replace(/\.box$/i, '')
}
